import { integerToAmount } from '@actual-app/core/shared/util';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import type { AccountEntity } from '@actual-app/core/types/models';

import {
  recortarInicioSinDatos,
  referenciaHabitual,
} from '#components/mobile/informes/estadisticaRobusta';

/**
 * «Cuánto ingresar a la cuenta común» para el mes siguiente: categorías de la
 * cuenta (de los pagos fijos o, de respaldo, de la preferencia sincronizada
 * `cuenta-comun-categorias`, JSON array de ids) y el cálculo de
 * `ingresoMesSiguiente`.
 */

export function leerCategoriasComunes(
  raw: string | undefined | null,
): string[] {
  if (!raw) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return [
      ...new Set(parsed.filter((id): id is string => typeof id === 'string')),
    ];
  } catch {
    return [];
  }
}

export function alternarCategoriaComun(
  ids: readonly string[],
  id: string,
): string[] {
  return ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id];
}

function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();
}

/**
 * Cuentas del traspaso: origen = la personal («Cuenta Personal», «Compte
 * personal»…), destino = la común («Conte conjunt», «Cuenta común»…). Solo
 * cuentas abiertas. `null` si no se encuentra alguna.
 */
export function cuentasDelTraspaso(cuentas: readonly AccountEntity[]): {
  origen: AccountEntity | null;
  destino: AccountEntity | null;
} {
  const abiertas = cuentas.filter(c => !c.closed);
  const destino =
    abiertas.find(c => /conjunt|comun|compartid/.test(normalizar(c.name))) ??
    null;
  const origen =
    abiertas.find(c => c !== destino && /personal/.test(normalizar(c.name))) ??
    null;
  return { origen, destino };
}

/**
 * Enlace a «nueva transacción» con el traspaso rellenado: en la cuenta de
 * origen, beneficiario = la cuenta destino (el beneficiario de traspaso se
 * llama como la cuenta) y el importe como salida.
 */
export function enlaceTraspaso({
  origen,
  destino,
  importe,
  nota,
}: {
  origen: string;
  destino: string;
  importe: IntegerAmount;
  nota?: string;
}): string {
  const params = new URLSearchParams({
    account: origen,
    payee: destino,
    amount: String(integerToAmount(importe)),
  });
  if (nota) {
    params.set('notes', nota);
  }
  return `/transactions/new?${params.toString()}`;
}

/** Meses completos que entran en el gasto previsto. */
export const MESES_PREVISTO = 12;

const CLAVE_MES = /^\d{4}-\d{2}$/;

/**
 * Valores crudos de la pref `cuenta-comun-traspasado`: por mes, `true`
 * (marcado sin importe, formato antiguo) o los céntimos que tocaba ingresar
 * cuando se marcó (para avisar si lo asignado cambia después).
 */
function leerValoresTraspaso(
  raw: string | undefined | null,
): Record<string, true | IntegerAmount> {
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const resultado: Record<string, true | IntegerAmount> = {};
    for (const [mes, valor] of Object.entries(parsed)) {
      if (!CLAVE_MES.test(mes)) {
        continue;
      }
      if (valor === true) {
        resultado[mes] = true;
      } else if (typeof valor === 'number' && Number.isInteger(valor)) {
        resultado[mes] = valor;
      }
    }
    return resultado;
  } catch {
    return {};
  }
}

/**
 * Estado «Traspasado» por mes: pref sincronizada `cuenta-comun-traspasado`
 * (JSON `{ "2026-10": 125000 }`; `true` en el formato antiguo).
 */
export function leerTraspasados(
  raw: string | undefined | null,
): Record<string, boolean> {
  const resultado: Record<string, boolean> = {};
  for (const mes of Object.keys(leerValoresTraspaso(raw))) {
    resultado[mes] = true;
  }
  return resultado;
}

/**
 * Traspaso de un mes: si está marcado y cuánto tocaba ingresar al marcarlo
 * (`null` si se marcó antes de guardarse el importe).
 */
export function leerTraspasoDelMes(
  raw: string | undefined | null,
  mes: string,
): { traspasado: boolean; importe: IntegerAmount | null } {
  const valor = leerValoresTraspaso(raw)[mes];
  if (valor === undefined) {
    return { traspasado: false, importe: null };
  }
  return { traspasado: true, importe: valor === true ? null : valor };
}

/**
 * Devuelve el JSON de la pref con el mes marcado o desmarcado. Al marcar con
 * `importe` se guarda lo que tocaba ingresar en ese momento.
 */
export function marcarTraspasado(
  raw: string | undefined | null,
  mes: string,
  traspasado: boolean,
  importe?: IntegerAmount,
): string {
  const actual = leerValoresTraspaso(raw);
  if (traspasado) {
    actual[mes] = importe ?? true;
  } else {
    delete actual[mes];
  }
  return JSON.stringify(actual);
}

/**
 * Gasto previsto al mes: mediana robusta (sin atípicos) de las salidas
 * mensuales de la cuenta en los `meses` meses completos anteriores a `mes`.
 * `salidas` da, por mes 'yyyy-MM', el total de salidas en positivo y en
 * céntimos (sin traspasos entre cuentas propias). Los meses iniciales sin
 * movimientos no cuentan (la cuenta aún no se usaba).
 */
export function gastoPrevisto(
  salidas: Readonly<Record<string, number>>,
  mesesAnteriores: readonly string[],
): { previsto: IntegerAmount; meses: number } {
  const serie = recortarInicioSinDatos(
    mesesAnteriores.map(mes => Math.abs(salidas[mes] ?? 0)),
  );
  if (serie.length === 0) {
    return { previsto: 0, meses: 0 };
  }
  return {
    previsto: referenciaHabitual(serie).referencia,
    meses: serie.length,
  };
}

/**
 * Categorías de la cuenta común: las mismas que el filtro de cuenta del Plan
 * (`deLaCuenta`: su cuenta es la común, por elección o por historial) más las
 * de los pagos fijos que se cargan en ella. Así entran también las que mueven
 * dinero sin ser un pago fijo (p. ej. «Moviments», con asignados negativos).
 * Si no hay ninguna, la elección manual (`cuenta-comun-categorias`) hace de
 * respaldo.
 */
export function categoriasCuentaComun({
  pagos,
  cuentaComunId,
  manuales,
  deLaCuenta = [],
}: {
  pagos: readonly { id: string; cuentaId: string | null }[];
  cuentaComunId: string | null | undefined;
  manuales: readonly string[];
  deLaCuenta?: readonly string[];
}): { ids: string[]; automaticas: boolean } {
  const ids = cuentaComunId
    ? [
        ...new Set([
          ...pagos.filter(p => p.cuentaId === cuentaComunId).map(p => p.id),
          ...deLaCuenta,
        ]),
      ]
    : [];
  return ids.length > 0
    ? { ids, automaticas: true }
    : { ids: [...manuales], automaticas: false };
}

export type IngresoMesSiguiente = {
  /** Lo que necesitará la cuenta el mes siguiente. */
  necesita: IntegerAmount;
  /** `true` si el mes siguiente aún no tiene nada asignado y se usa el gasto habitual. */
  estimado: boolean;
  /** Lo asignado en positivo el mes siguiente (lo que se podrá gastar). */
  asignadoGasto: IntegerAmount;
  /** Lo que sigue apartado este mes en esas categorías (aún por pagar o guardado). */
  pendiente: IntegerAmount;
  /** Lo que llega al mes siguiente: saldo de hoy − pendiente. */
  llega: IntegerAmount;
  /** Lo que hay que ingresar (0 si ya llega). */
  ingresar: IntegerAmount;
  /** Lo asignado se queda por debajo de lo que se suele gastar. */
  corto: boolean;
  /** Lo que habría que ingresar para cubrir el gasto habitual. */
  ingresarHabitual: IntegerAmount;
};

/**
 * Cuánto ingresar en la cuenta común para el mes siguiente (cómo lo hace
 * Vadym al cobrar, hacia el 28): lo asignado el mes siguiente en sus
 * categorías (neto, con los negativos) − (saldo de hoy − lo que aún queda este
 * mes). Lo que queda es el disponible positivo de esas categorías este mes
 * (por pagar o guardado, como «Moviments»). Si el
 * mes siguiente aún no está asignado, se usa el gasto habitual (`previsto`).
 */
export function ingresoMesSiguiente({
  asignadoSiguiente,
  disponibleEsteMes,
  saldo,
  previsto,
}: {
  asignadoSiguiente: readonly IntegerAmount[];
  disponibleEsteMes: readonly IntegerAmount[];
  saldo: IntegerAmount;
  previsto: IntegerAmount;
}): IngresoMesSiguiente {
  // Neto: lo movido entre categorías (asignados negativos, p. ej. sacar de
  // «Moviments» para repartir) ya está en la cuenta y no hay que ingresarlo.
  const asignado = Math.max(
    0,
    asignadoSiguiente.reduce((s, v) => s + v, 0),
  );
  const asignadoGasto = asignadoSiguiente.reduce(
    (s, v) => s + Math.max(0, v),
    0,
  );
  const estimado = asignadoSiguiente.every(v => v === 0);
  const necesita = estimado ? previsto : asignado;
  const pendiente = disponibleEsteMes.reduce((s, v) => s + Math.max(0, v), 0);
  const llega = saldo - pendiente;
  return {
    necesita,
    estimado,
    asignadoGasto,
    pendiente,
    llega,
    ingresar: Math.max(0, necesita - llega),
    corto: !estimado && previsto > 0 && asignadoGasto < previsto,
    ingresarHabitual: Math.max(0, previsto - llega),
  };
}
